import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getToken, deleteToken } from '../../services/auth';
import { updateProductInventory, deleteProductInventory, getProductImages, uploadProductImage, deleteProductImage, getBaseProducts, getProductDetail } from '../../api/inventories';
import { getDishAttributes } from '../../api/dishAttributes';
import { JsonEditorWrapper } from './JsonEditorWrapper';
import { buildDishAttributeSchema } from './schemaBuilder';
import { updateProductBasicInfo } from '../../features/inventory/inventorySlice';
import { Button } from '../../constants/base';
import LogoShapya from '../../assets/Logo_ShapYa_Red.png';
import Divider from '@material-ui/core/Divider';
import {
  Box,
  FormControlLabel,
  Typography,
  Container,
  Paper,
} from '@material-ui/core';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import Switch from '@material-ui/core/Switch';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, TextField } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ContentWrapper } from '../../shared/ContentWrapper/ContentWrapper';

const formatDateTimeForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

export const EditProduct = () => {
  const { t } = useTranslation();
  const { id: urlProductId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [inventoryProduct, setInventoryProduct] = useState(
    location.state && location.state.inventoryProduct
  );
  
  const [isFetchingProduct, setIsFetchingProduct] = useState(!inventoryProduct);

  const storeId = useSelector((state) => state.store.id);

  const [sbProductId, setSbProductId] = useState(
    inventoryProduct ? inventoryProduct.storeBranchProductId : undefined,
  );
  const [productIdValue, setProductIdValue] = useState(
    (inventoryProduct && inventoryProduct.product && inventoryProduct.product.id) || null,
  );
  const [priceValue, setPriceValue] = useState(
    (inventoryProduct && inventoryProduct.fullPrice) || '',
  );
  const [nameValue, setNameValue] = useState(
    (inventoryProduct && inventoryProduct.name) || '',
  );
  const [bestSellingValue, setBestSellingValue] = useState(
    (inventoryProduct && inventoryProduct.bestSelling) || false,
  );
  const [activeValue, setActiveValue] = useState(
    (inventoryProduct && inventoryProduct.active) || false,
  );
  const [skuValue, setSkuValue] = useState((inventoryProduct && inventoryProduct.sku) || '');
  const [descriptionValue, setDescriptionValue] = useState(
    (inventoryProduct && inventoryProduct.description) || '',
  );
  const [discountValue, setDiscountValue] = useState(
    (inventoryProduct && inventoryProduct.discountPercent) || 0,
  );
  
  const [baseProducts, setBaseProducts] = useState([]);
  const [selectedBaseProduct, setSelectedBaseProduct] = useState(null);
  
  const [productImages, setProductImages] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [startDiscountDateValue, setStartDiscountDateValue] = useState(
    formatDateTimeForInput(
      inventoryProduct && inventoryProduct.discountInitDate,
    ),
  );
  const [endDiscountDateValue, setEndDiscountDateValue] = useState(
    formatDateTimeForInput(
      inventoryProduct && inventoryProduct.discountEndDate,
    ),
  );

  const [disable, setDisable] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState(null);

  const [dishAttributes, setDishAttributes] = useState({});
  const [dishAttributesSchema, setDishAttributesSchema] = useState(null);

  const fetchDishAttributes = async () => {
    try {
      const response = await getDishAttributes({ storeId, token: getToken() });
      const rawAttrs = response.results || response;
      const schema = buildDishAttributeSchema(rawAttrs);
      setDishAttributesSchema(schema);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    if (storeId) {
      fetchDishAttributes();
    }
  }, [storeId]);

  React.useEffect(() => {
    if (inventoryProduct) {
      const attrs = inventoryProduct.dishAttributes || inventoryProduct.dish_attributes || {};
      const rawAttrs = {};
      
      const camelToSnake = (str) => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

      const snakeAttrs = {};
      Object.keys(attrs).forEach(key => {
        snakeAttrs[camelToSnake(key)] = attrs[key];
      });

      Object.keys(snakeAttrs).forEach((key) => {
        if (!key.endsWith('_properties') && snakeAttrs[key] && Array.isArray(snakeAttrs[key].elements)) {
          // Un-reformat data returned by the backend product detail API
          rawAttrs[key] = snakeAttrs[key].elements;
          const { elements, ...props } = snakeAttrs[key];
          rawAttrs[`${key}_properties`] = [props];
        } else if (Array.isArray(snakeAttrs[key])) {
          // Data is already in raw array format
          rawAttrs[key] = snakeAttrs[key];
        }
      });
      setDishAttributes(rawAttrs);
    }
  }, [inventoryProduct]);

  const loadImages = async () => {
    try {
      const response = await getProductImages({ storeId, productId: sbProductId, token: getToken() });
      if (response && response.results) {
        setProductImages(response.results);
      } else if (Array.isArray(response)) {
        setProductImages(response);
      }
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };

  React.useEffect(() => {
    if (sbProductId) {
      loadImages();
    }
  }, [sbProductId]);

  const fetchBaseProducts = async (searchWord = '') => {
    try {
      const response = await getBaseProducts(getToken(), searchWord);
      setBaseProducts(response.results || response);
    } catch (error) {
      console.error('Error fetching base products:', error);
    }
  };

  React.useEffect(() => {
    fetchBaseProducts();
  }, []);

  const fetchProductDetail = async () => {
    try {
      setIsFetchingProduct(true);
      const response = await getProductDetail({
        storeId,
        productId: urlProductId || sbProductId,
        token: getToken(),
      });
      setInventoryProduct(response);
      setSbProductId(response.storeBranchProductId);
      setProductIdValue((response.product && response.product.id) || null);
      setPriceValue(response.fullPrice || '');
      setNameValue(response.name || '');
      setBestSellingValue(response.bestSelling || false);
      setActiveValue(response.active || false);
      setSkuValue(response.sku || '');
      setDescriptionValue(response.description || '');
      setDiscountValue(response.discountPercent || 0);
      setStartDiscountDateValue(formatDateTimeForInput(response.discountInitDate));
      setEndDiscountDateValue(formatDateTimeForInput(response.discountEndDate));
    } catch (error) {
      console.error('Error fetching product details:', error);
    } finally {
      setIsFetchingProduct(false);
    }
  };

  React.useEffect(() => {
    if (urlProductId && storeId) {
      fetchProductDetail();
    } else if (!urlProductId) {
      setIsFetchingProduct(false);
    }
  }, [urlProductId, storeId]);

  if (isFetchingProduct) {
    return (
      <ContentWrapper>
        <Container style={{ marginTop: '40px', textAlign: 'center' }}>
          <Typography variant="h6">Loading product details...</Typography>
        </Container>
      </ContentWrapper>
    );
  }

  if (!inventoryProduct) {
    return (
      <ContentWrapper>
        <Container style={{ marginTop: '40px', textAlign: 'center' }}>
          <Typography variant="h5">Product not found.</Typography>
          <Button onClick={() => navigate('/inventory')} style={{ marginTop: '20px' }}>
            Go back to Inventory
          </Button>
        </Container>
      </ContentWrapper>
    );
  }

  const handleUpdateProductPrice = async () => {
    try {
      setDisable(true);
      setApiError(null);
      setApiSuccess(null);

      const initDate = startDiscountDateValue
        ? new Date(startDiscountDateValue).toISOString()
        : null;
      const endDate = endDiscountDateValue
        ? new Date(endDiscountDateValue).toISOString()
        : null;

      const updateProductInfo = await updateProductInventory({
        id: storeId,
        token: getToken(),
        productId: sbProductId,
        params: {
          price: priceValue,
          discount: discountValue,
          sku: skuValue,
          name: nameValue,
          description:
            descriptionValue === '' ? 'No description' : descriptionValue,
          best_selling: bestSellingValue,
          active: activeValue,
          discount_init_date: initDate,
          discount_end_date: endDate,
          dish_attributes: dishAttributes,
          ...(productIdValue && { product_id: productIdValue }),
        },
      });
      await dispatch(updateProductBasicInfo(updateProductInfo));
      setApiSuccess('Product updated successfully!');
      setDisable(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        deleteToken();
        return;
      }
      let errorMessage = (error.response && error.response.data && error.response.data.message) || 'An error occurred';
      if (error.response && error.response.data && typeof error.response.data === 'object' && !error.response.data.message) {
        const errors = [];
        for (const [key, value] of Object.entries(error.response.data)) {
          if (Array.isArray(value)) {
            errors.push(`${key}: ${value.join(', ')}`);
          } else if (typeof value === 'string') {
            errors.push(`${key}: ${value}`);
          }
        }
        if (errors.length > 0) {
          errorMessage = `An error occurred: ${errors.join(' | ')}`;
        }
      }
      setApiError(errorMessage);
      console.error(error);
      setDisable(false);
    }
  };

  const handleDeleteProduct = async () => {
    try {
      await deleteProductInventory({
        id: storeId,
        productId: sbProductId,
        token: getToken(),
      });
      setDeleteModalOpen(false);
      navigate('/inventory');
    } catch (error) {
      console.error(error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setIsUploadingImage(true);
      await uploadProductImage({
        storeId,
        productId: sbProductId,
        file,
        token: getToken(),
      });
      await loadImages();
    } catch (error) {
      console.error('Error uploading image:', error);
      setApiError('Failed to upload image.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await deleteProductImage({
        storeId,
        productId: sbProductId,
        imageId,
        token: getToken(),
      });
      await loadImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      setApiError('Failed to delete image.');
    }
  };

  const handleFieldChange = (setter) => (e) => {
    if (apiError) setApiError(null);
    if (apiSuccess) setApiSuccess(null);
    setter(e.target.value);
  };

  const handleCancel = () => {
    navigate('/inventory');
  };

  return (
    <ContentWrapper>
      <Container
          maxWidth={false}
          style={{ maxWidth: '1400px', marginTop: '40px', paddingBottom: '40px' }}>
          <Paper
            elevation={3}
            style={{ padding: '30px', borderRadius: '10px' }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}>
              <img src={LogoShapya} width="120" height="36" alt="Shapya Logo" />
              <Typography variant="h5" component="h2">
                {t('Inventory.modal.productBasicInfoUpdate', 'Update Product')}
              </Typography>
            </Box>
            <Divider />

            <Box
              mt={3}
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center">
              <Box>
                <Typography variant="h6">{inventoryProduct.name}</Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  SKU: {inventoryProduct.sku}
                </Typography>
              </Box>
              {inventoryProduct.images && inventoryProduct.images[0] && (
                <img
                  src={inventoryProduct.images[0].image}
                  alt={inventoryProduct.name}
                  style={{ width: '80px', borderRadius: '5px' }}
                />
              )}
            </Box>

            <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap="20px">
              <FormControl fullWidth variant="outlined" style={{ gridColumn: 'span 2' }}>
                <Autocomplete
                  options={baseProducts}
                  getOptionLabel={(option) => option.name || ''}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelectedBaseProduct(newValue);
                      setProductIdValue(newValue.id);
                      setNameValue(newValue.name);
                      if (newValue.sku) setSkuValue(newValue.sku);
                      if (newValue.price && newValue.price !== false) setPriceValue(newValue.price);
                      if (newValue.description) setDescriptionValue(newValue.description);
                    } else {
                      setSelectedBaseProduct(null);
                      setProductIdValue(null);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (apiError) setApiError(null);
                    if (apiSuccess) setApiSuccess(null);
                    fetchBaseProducts(newInputValue);
                    if (!newInputValue) {
                      setSelectedBaseProduct(null);
                      setProductIdValue(null);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('Inventory.modal.productBasicInfoUpdate.searchBaseProduct', 'Search Base Product')}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
                <Typography variant="caption" color="textSecondary" style={{ marginTop: '4px' }}>
                  Tip: Search for a base product to load its default data. You can then modify any fields before saving.
                </Typography>
              </FormControl>

              <FormControl fullWidth variant="outlined">
                {selectedBaseProduct && selectedBaseProduct.name && (
                  <Typography variant="caption" color="textSecondary" style={{ marginBottom: '4px' }}>
                    Base product value: {selectedBaseProduct.name}
                  </Typography>
                )}
                <InputLabel htmlFor="outlined-adornment-name">
                  {t(
                    'Inventory.modal.productBasicInfoUpdate.inputProductName',
                    'Product Name',
                  )}
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-name"
                  type="text"
                  value={nameValue}
                  autoComplete="off"
                  onChange={(e) => {
                    handleFieldChange(setNameValue)(e);
                    e.target.value === '' || priceValue === ''
                      ? setDisable(true)
                      : setDisable(false);
                  }}
                  labelWidth={110}
                  inputProps={{ maxLength: 120 }}
                />
              </FormControl>

              <FormControl fullWidth variant="outlined">
                {selectedBaseProduct && selectedBaseProduct.sku && (
                  <Typography variant="caption" color="textSecondary" style={{ marginBottom: '4px' }}>
                    Base product value: {selectedBaseProduct.sku}
                  </Typography>
                )}
                <InputLabel htmlFor="outlined-adornment-sku">
                  SKU
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-sku"
                  type="text"
                  value={skuValue}
                  autoComplete="off"
                  onChange={handleFieldChange(setSkuValue)}
                  labelWidth={40}
                  inputProps={{ maxLength: 50 }}
                />
              </FormControl>

              <FormControl fullWidth variant="outlined">
                {selectedBaseProduct && selectedBaseProduct.price && selectedBaseProduct.price !== false && (
                  <Typography variant="caption" color="textSecondary" style={{ marginBottom: '4px' }}>
                    Base product value: {selectedBaseProduct.price}
                  </Typography>
                )}
                <InputLabel htmlFor="outlined-adornment-amount">
                  {t(
                    'Inventory.modal.productBasicInfoUpdate.inputProductPrice',
                    'Original Price',
                  )}
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-amount"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={priceValue}
                  autoComplete="off"
                  onChange={(e) => {
                    handleFieldChange(setPriceValue)(e);
                    e.target.value === '' || nameValue === ''
                      ? setDisable(true)
                      : setDisable(false);
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      {inventoryProduct.currency &&
                        inventoryProduct.currency.code}
                      {inventoryProduct.currency &&
                        inventoryProduct.currency.symbol}
                    </InputAdornment>
                  }
                  labelWidth={110}
                />
              </FormControl>

              <FormControl
                fullWidth
                variant="outlined"
                style={{ gridColumn: 'span 2' }}>
                {selectedBaseProduct && selectedBaseProduct.description && (
                  <Typography variant="caption" color="textSecondary" style={{ marginBottom: '4px' }}>
                    Base product value: {selectedBaseProduct.description}
                  </Typography>
                )}
                <InputLabel htmlFor="outlined-adornment-description">
                  {t(
                    'Inventory.modal.productBasicInfoUpdate.inputProductDescription',
                    'Description',
                  )}
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-description"
                  type="text"
                  multiline
                  rows={3}
                  value={descriptionValue}
                  autoComplete="off"
                  onChange={handleFieldChange(setDescriptionValue)}
                  labelWidth={100}
                  inputProps={{ maxLength: 520 }}
                />
              </FormControl>

              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="outlined-adornment-discount">
                  {t(
                    'Inventory.modal.productBasicInfoUpdate.inputProductDiscountPercent',
                    'Discount %',
                  )}
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-discount"
                  type="number"
                  inputProps={{ min: 0, max: 99 }}
                  value={discountValue}
                  autoComplete="off"
                  onChange={handleFieldChange(setDiscountValue)}
                  endAdornment={
                    <InputAdornment position="end">%</InputAdornment>
                  }
                  labelWidth={100}
                />
              </FormControl>
              <Box></Box>

              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="start-discount-date" shrink>
                  {t(
                    'Inventory.modal.productBasicInfoUpdate.inputStartDiscountDate',
                    'Start Discount',
                  )}
                </InputLabel>
                <OutlinedInput
                  id="start-discount-date"
                  type="datetime-local"
                  value={startDiscountDateValue}
                  onChange={handleFieldChange(setStartDiscountDateValue)}
                  labelWidth={120}
                  style={{ marginTop: '8px' }}
                />
              </FormControl>

              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="end-discount-date" shrink>
                  {t(
                    'Inventory.modal.productBasicInfoUpdate.inputEndDiscountDate',
                    'End Discount',
                  )}
                </InputLabel>
                <OutlinedInput
                  id="end-discount-date"
                  type="datetime-local"
                  value={endDiscountDateValue}
                  onChange={handleFieldChange(setEndDiscountDateValue)}
                  labelWidth={120}
                  style={{ marginTop: '8px' }}
                />
              </FormControl>
            </Box>

            <Box mt={4} display="flex" flexDirection="column" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={activeValue}
                    onChange={() => setActiveValue(!activeValue)}
                  />
                }
                label={
                  <Typography variant="subtitle1">
                    {t(
                      'Inventory.modal.productBasicInfoUpdate.switchProductAvailable',
                      'Available',
                    )}
                  </Typography>
                }
                labelPlacement="end"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={bestSellingValue}
                    onChange={() => setBestSellingValue(!bestSellingValue)}
                  />
                }
                label={
                  <Typography variant="subtitle1">
                    {t(
                      'Inventory.modal.productBasicInfoUpdate.switchIsBestSelling',
                      'Best Selling',
                    )}
                  </Typography>
                }
                labelPlacement="end"
              />
            </Box>

            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Product Images
              </Typography>
              <Divider />
              <Box mt={2} display="flex" flexWrap="wrap" gap="16px">
                {productImages.map((img) => (
                  <Box key={img.id} position="relative" border="1px solid #ccc" borderRadius="8px" padding="4px">
                    <img
                      src={img.image}
                      alt="Product"
                      style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                    <IconButton
                      size="small"
                      style={{ position: 'absolute', top: -10, right: -10, backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
                      onClick={() => handleDeleteImage(img.id)}
                    >
                      <DeleteIcon color="error" fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  border="2px dashed #ccc"
                  borderRadius="8px"
                  width="100px"
                  height="100px"
                  style={{ cursor: 'pointer', opacity: isUploadingImage ? 0.5 : 1 }}
                  component="label"
                >
                  <CloudUploadIcon color="action" />
                  <Typography variant="caption" color="textSecondary">Upload</Typography>
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploadingImage}
                  />
                </Box>
              </Box>
            </Box>

            {dishAttributesSchema && Object.keys(dishAttributesSchema.properties || {}).length > 0 && (
              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  Dish Options & Pricing
                </Typography>
                <Divider />
                <Box mt={2}>
                  <JsonEditorWrapper
                    schema={dishAttributesSchema}
                    value={dishAttributes}
                    onChange={setDishAttributes}
                  />
                </Box>
              </Box>
            )}

            {apiError && (
              <Box
                mt={3}
                p={2}
                style={{
                  backgroundColor: 'rgba(211, 47, 47, 0.1)',
                  borderRadius: '4px',
                  width: '100%',
                }}>
                <Typography color="error" variant="body2">
                  {apiError}
                </Typography>
              </Box>
            )}

            {apiSuccess && (
              <Box
                mt={3}
                p={2}
                style={{
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '4px',
                  width: '100%',
                }}>
                <Typography style={{ color: '#4caf50' }} variant="body2">
                  {apiSuccess}
                </Typography>
              </Box>
            )}

            <Box mt={4}>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                align="center">
                {t(
                  'Inventory.modal.productBasicInfoUpdateConfirmation',
                  'Are you sure you want to update this product?',
                )}
              </Typography>
            </Box>

            <Box mt={3} display="flex" justifyContent="center" gap={2}>
              <Button
                onClick={() => setDeleteModalOpen(true)}
                style={{
                  marginRight: '16px',
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                }}>
                Delete
              </Button>
              <Button
                disabled={disable}
                onClick={handleUpdateProductPrice}
                style={{
                  marginRight: '16px',
                  backgroundColor: '#1976d2',
                  color: '#fff',
                }}>
                {t(
                  'Inventory.modal.productBasicInfoUpdate.button.update',
                  'Update Product',
                )}
              </Button>
              <Button
                onClick={handleCancel}
                style={{
                  backgroundColor: '#e0e0e0',
                  color: '#333',
                }}>
                {t(
                  'Inventory.modal.productBasicInfoUpdate.button.ok',
                  'OK',
                )}
              </Button>
            </Box>
          </Paper>
        </Container>

        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
          <DialogTitle>Delete Product</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete {nameValue}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteModalOpen(false)} style={{ backgroundColor: '#e0e0e0', color: '#333' }}>
              Cancel
            </Button>
            <Button onClick={handleDeleteProduct} style={{ backgroundColor: '#d32f2f', color: '#fff' }}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
    </ContentWrapper>
  );
};
